import React from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../components/LegalNav';
import SEO from '../components/SEO';
import '../styles/LegalPages.css';

const ShippingPolicy = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="legal-page-container">
      <SEO
        title="Shipping & Returns Policy"
        description="Star.Ryhwa official shipping and returns policy. Processing times, packaging safety, tracked shipping, and delivery estimates."
        canonicalUrl="/shipping-policy"
      />
      <div className="legal-page-inner">
        {/* Breadcrumb Bar */}
        <div className="legal-breadcrumbs">
          <Link to="/">Home</Link>
          <span className="legal-breadcrumb-separator">/</span>
          <span>Trust &amp; Legal Center</span>
          <span className="legal-breadcrumb-separator">/</span>
          <span className="legal-breadcrumb-current">Shipping &amp; Returns Policy</span>
        </div>

        {/* Document Header */}
        <header className="legal-doc-header">
          <h1 className="legal-doc-title">Shipping &amp; Returns Policy</h1>
          <p className="legal-doc-description">
            Complete guide to Star.Ryhwa's order processing schedules, packaging standards, stamped mail risk allocation, international customs, and delivery verification procedures.
          </p>

          <div className="legal-meta-bar">
            <div className="legal-meta-items">
              <span className="legal-meta-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Last Updated: <strong>September 2026</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Estimated Read: <strong>4 min read</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="1" y="3" width="15" height="13"/>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 8"/>
                  <circle cx="5.5" cy="18.5" r="2.5"/>
                  <circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
                Primary Carrier: <strong>USPS Ground Advantage</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                Packaging: <strong>Rigid Toploaders &amp; Mailers</strong>
              </span>
            </div>
            <button type="button" onClick={handlePrint} className="legal-print-btn" title="Print or Save as PDF">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print / Save PDF
            </button>
          </div>
        </header>

        {/* Navigation Tab Bar */}
        <LegalNav />

        {/* Executive Summary */}
        <div className="legal-doc-summary">
          <h2>Summary of Delivery &amp; Returns Guidelines</h2>
          <ul>
            <li><strong>Tracked vs. Stamped Mail:</strong> We strongly advise Tracked Shipping (USPS Ground Advantage with barcode tracking &amp; postal insurance). Economy stamped mail carries zero tracking or insurance and risk of loss is assumed by the buyer.</li>
            <li><strong>All Sales Final:</strong> Due to collectible grading and custom crafting, returns and cancellations are not accepted.</li>
            <li><strong>7-Day Damage Verification Window:</strong> In the rare event a tracked parcel arrives damaged, you must email an unedited, continuous package unboxing video within 7 days of delivery.</li>
            <li><strong>International Duties:</strong> Customers outside the U.S. act as the importer of record and are responsible for destination customs, VAT, and tariffs.</li>
          </ul>
        </div>

        {/* Main Two-Column Layout */}
        <div className="legal-grid-layout">
          {/* Main Content Column */}
          <article className="legal-main-content">
            {/* Section 1 */}
            <section id="section-1" className="legal-doc-section">
              <h2>1. Shipping Scope &amp; Carriers</h2>
              <p>
                Star.Ryhwa ships nationwide across all <strong>50 United States</strong> (including Hawaii, Alaska, Puerto Rico, and APO/FPO military addresses) as well as <strong>internationally worldwide</strong> to select destinations.
              </p>
              <p>
                Orders are dispatched via the United States Postal Service (USPS). Depending on the shipping method chosen at checkout, packages are sent either as <strong>Tracked Shipping (USPS Ground Advantage / Bubble Mailer)</strong> or <strong>Untracked Economy Stamped Mail</strong>.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="legal-doc-section">
              <h2>2. Stamped Shipping Disclaimer (Untracked Mail)</h2>
              <div className="legal-doc-note">
                <p>
                  <strong>Untracked Stamped Mail Risk Allocation:</strong> Stamped postage (untracked envelope with postage stamp) is offered exclusively as an affordable, budget-friendly option for small lightweight items (single photocards and sticker packs). 
                  <strong> Stamped mail carries NO tracking number, scan updates, delivery confirmation, or postal insurance.</strong>
                </p>
              </div>
              <p>
                Once an untracked stamped package has been deposited with and accepted by the USPS, <strong>Star.Ryhwa has zero control over postal handling and assumes no liability for lost, stolen, delayed, or transit-damaged stamped mail. The risk of loss is strictly assumed by the buyer.</strong>
              </p>
              <p>
                If you want guaranteed delivery scans, live tracking updates, and carrier insurance protection, you must select <strong>Tracked Shipping</strong> at checkout.
              </p>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="legal-doc-section">
              <h2>3. Address Accuracy &amp; Reshipment Fees</h2>
              <p>
                Customers are solely responsible for entering a complete, deliverable, and accurate delivery address during checkout—including all applicable apartment, suite, unit, or box numbers.
              </p>
              <div className="legal-doc-note">
                <p>
                  <strong>Returned Packages (Insufficient Address / Return to Sender):</strong> If a package is returned to our studio by the postal carrier due to an incomplete, inaccurate, or outdated address provided by the customer, the customer is responsible for paying the actual re-shipment postage before the package will be dispatched again.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="legal-doc-section">
              <h2>4. Delivery Confirmation &amp; Mailbox Theft (Porch Piracy)</h2>
              <p>
                For all tracked shipments, once the postal carrier (USPS) marks a package as <strong>"Delivered"</strong> with official carrier timestamp and GPS scan data:
              </p>
              <ul>
                <li>Ownership and physical risk of loss transfer entirely to the recipient.</li>
                <li>Star.Ryhwa is not responsible for packages stolen from mailboxes, doorsteps, apartment lobbies, or porch areas following carrier delivery.</li>
                <li>In the event of suspected mail theft following delivery confirmation, customers should promptly report the incident to their local post office and file a mail theft complaint with the U.S. Postal Inspection Service.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="section-5" className="legal-doc-section">
              <h2>5. International Shipping, Customs Duties &amp; Import VAT</h2>
              <p>
                We are thrilled to ship handmade pieces to our international community! When ordering outside the United States, please note:
              </p>
              <div className="legal-doc-note">
                <p>
                  <strong>Customs &amp; Import Tariffs:</strong> International recipients act as the importer of record and are solely responsible for all customs duties, import tariffs, value-added taxes (VAT), goods and services taxes (GST), and brokerage clearance fees assessed by local authorities upon entry into your country.
                </p>
              </div>
              <ul>
                <li><strong>Import Fees Excluded:</strong> Customs duties and import taxes are never collected by Star.Ryhwa at checkout.</li>
                <li><strong>Clearance Delays:</strong> Star.Ryhwa is not liable for border inspection holds or international customs clearance delays.</li>
                <li><strong>Abandoned or Refused Packages:</strong> If an international shipment is refused by the buyer or returned due to unpaid import fees, original shipping fees are strictly non-refundable.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section id="section-6" className="legal-doc-section">
              <h2>6. Production &amp; Order Processing Times</h2>
              <p>
                Every Star.Ryhwa parcel is packaged with collector-grade care (rigid toploaders, protective sleeves, and secure mailers):
              </p>
              <ul>
                <li><strong>In-Stock Collectibles &amp; Stickers:</strong> Packaged and shipped within <strong>1–3 business days</strong>.</li>
                <li><strong>Custom Handmade Crafts (Collages &amp; Charms):</strong> Assembled to order; require <strong>3–7 business days</strong> of crafting and curing time prior to shipment.</li>
              </ul>
              <p>
                For tracked packages, an automated email notification with your active USPS tracking number will be sent as soon as your shipping label is generated.
              </p>
            </section>

            {/* Section 7 */}
            <section id="section-7" className="legal-doc-section">
              <h2>7. Returns &amp; Refunds Policy — All Sales Are Final</h2>
              <div className="legal-doc-note">
                <p>
                  <strong>All Sales Are Final:</strong> Due to the exclusive collectible nature of K-Pop goods and the made-to-order assembly of handmade pieces, <strong>we do not accept returns, exchanges, or order cancellations once submitted.</strong>
                </p>
              </div>
              <p>
                Please review all product images, photocard condition gradings, and inclusion lists carefully before completing your order.
              </p>
              
              <h3>Exceptions: Incorrect Items or Damage on Tracked Packages</h3>
              <p>
                If you receive an incorrect item or if a <strong>tracked</strong> parcel arrives damaged, claims must be submitted within <strong>7 days of delivery</strong> with the following verification:
              </p>
              <ul>
                <li>Your full order number and customer name.</li>
                <li>A continuous, unedited unboxing video showing the sealed exterior mailer and the parcel opening process.</li>
                <li>Clear, focused photographs of the incorrect or damaged merchandise.</li>
              </ul>
              <p>
                <em>Note: Claims on untracked stamped mail cannot be accepted or reimbursed, as stamped postage carries no carrier insurance or delivery verification.</em>
              </p>
            </section>

            {/* Section 8 */}
            <section id="section-8" className="legal-doc-section">
              <h2>8. Shipping Inquiries &amp; Customer Support</h2>
              <p>
                For shipping inquiries, combined order assistance, or international delivery questions, please reach out to our team:
              </p>
              <ul className="legal-contact-list">
                <li><strong>Email:</strong> <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a></li>
                <li><strong>Instagram:</strong> <a href="https://www.instagram.com/star.ryhwa" target="_blank" rel="noopener noreferrer">@star.ryhwa</a></li>
                <li><strong>Studio Location:</strong> State of Hawaii, United States</li>
              </ul>
            </section>
          </article>

          {/* Sticky Table of Contents Sidebar */}
          <aside className="legal-sidebar">
            <div className="legal-toc-container">
              <div className="legal-toc-title">On This Page</div>
              <ul className="legal-toc-list">
                <li><a href="#section-1" className="legal-toc-link">1. Shipping Scope</a></li>
                <li><a href="#section-2" className="legal-toc-link">2. Stamped Mail Risk</a></li>
                <li><a href="#section-3" className="legal-toc-link">3. Address Accuracy</a></li>
                <li><a href="#section-4" className="legal-toc-link">4. Delivery &amp; Porch Theft</a></li>
                <li><a href="#section-5" className="legal-toc-link">5. International Customs</a></li>
                <li><a href="#section-6" className="legal-toc-link">6. Processing Times</a></li>
                <li><a href="#section-7" className="legal-toc-link">7. All Sales Final &amp; Claims</a></li>
                <li><a href="#section-8" className="legal-toc-link">8. Customer Support</a></li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;
